import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { getCdnUrl } from '../utils/cdnConfig';

/**
 * SEO Component for managing meta tags dynamically
 * 
 * @param {string} page - The page identifier for translations (e.g., 'home', 'about', 'products')
 * @param {object} data - Optional dynamic data for interpolation (e.g., profile name, dates)
 * @param {string} customTitle - Optional custom title override
 * @param {string} customDescription - Optional custom description override
 * @param {string} customKeywords - Optional custom keywords override
 * @param {string} customImage - Optional custom OG image URL (for specific pages/profiles)
 */
const SEO = ({ page = 'home', data = {}, customTitle, customDescription, customKeywords, customImage }) => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  
  // Get current language from URL or fallback to i18n
  const currentLang = lang || i18n.language || 'mk';
  
  // Base URL for canonical links
  const baseUrl = 'https://spomen.mk';
  
  // Get translations for SEO
  const title = customTitle || t(`seo.${page}.title`, data);
  const description = customDescription || t(`seo.${page}.description`, data);
  const keywords = customKeywords || t(`seo.${page}.keywords`, data);
  
  // Build canonical URL
  const canonicalUrl = `${baseUrl}${location.pathname}`;
  
  // Open Graph Image - Instagram/Facebook optimized (1200x630 - 1.91:1 ratio)
  // Transform custom image through CDN if provided
  const transformedCustomImage = customImage ? getCdnUrl(customImage) : null;
  const ogImage = transformedCustomImage || `${baseUrl}/assets/og-image-1200x630.jpg`;
  const ogImageWidth = '1200';
  const ogImageHeight = '630';
  
  // Language code mapping for hreflang
  const langMap = {
    'mk': 'mk-MK',
    'al': 'sq-AL',
    'en': 'en-US',
    'tr': 'tr-TR',
    'hr': 'hr-HR',
    'sr': 'sr-RS',
    'de': 'de-DE'
  };
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={currentLang} />
      <meta name="author" content="Spomen MK" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang tags for multilingual support */}
      <link rel="alternate" hrefLang="mk" href={`${baseUrl}/mk${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="al" href={`${baseUrl}/al${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}/en${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="tr" href={`${baseUrl}/tr${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="hr" href={`${baseUrl}/hr${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="sr" href={`${baseUrl}/sr${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="de" href={`${baseUrl}/de${location.pathname.replace(`/${currentLang}`, '')}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/mk${location.pathname.replace(`/${currentLang}`, '')}`} />
      
      {/* Open Graph / Facebook - Instagram Optimized */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content={ogImageWidth} />
      <meta property="og:image:height" content={ogImageHeight} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={langMap[currentLang] || 'mk-MK'} />
      <meta property="og:site_name" content="Spomen MK" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:image:alt" content={title} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Geo Tags for local SEO */}
      <meta name="geo.region" content="MK" />
      <meta name="geo.placename" content="Skopje" />
      <meta name="geo.position" content="41.9973;21.4280" />
      <meta name="ICBM" content="41.9973, 21.4280" />
    </Helmet>
  );
};

export default SEO;
